import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Eye, EyeOff, KeyRound, CheckCircle, XCircle } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();

  const token = searchParams.get("token");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("form"); // "form" | "success" | "invalid" | "expired"
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) setStatus("invalid");
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/auth/reset-password`,
        { token, new_password: newPassword },
        { withCredentials: true }
      );
      login(res.data.user, res.data.token);
      setStatus("success");
      setTimeout(() => navigate("/dashboard"), 2000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "token_expired") {
        setStatus("expired");
      } else if (detail === "invalid_token") {
        setStatus("invalid");
      } else if (detail === "password_too_short") {
        setError("Password must be at least 6 characters.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left Panel */}
      <div className="hidden lg:flex w-1/2 gradient-hero flex-col justify-center px-16">
        <div className="max-w-md">
          <div className="mb-10">
            <div style={{width:'160px', height:'42px', overflow:'hidden', position:'relative'}}>
              <img
                src="https://customer-assets.emergentagent.com/job_workpro-ai/artifacts/aayaszwu_imresizer-Promptlee.png"
                alt="Promptlee"
                style={{width:'215px', position:'absolute', top:'50%', left:'50%', transform:'translate(-50%, -50%)'}}
              />
            </div>
          </div>
          <h2 className="text-3xl font-bold text-slate-900 mb-4 leading-tight">
            AI Applications for Every Business Challenge
          </h2>
          <p className="text-slate-600 leading-relaxed">
            Access structured AI prompts for Procurement, Finance, Marketing, HR, Sales, and Operations.
          </p>
        </div>
      </div>

      {/* Right Panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">

          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <div style={{width:'160px', height:'42px', overflow:'hidden', position:'relative'}}>
              <img
                src="https://customer-assets.emergentagent.com/job_workpro-ai/artifacts/aayaszwu_imresizer-Promptlee.png"
                alt="Promptlee"
                style={{width:'215px', position:'absolute', top:'50%', left:'50%', transform:'translate(-50%, -50%)'}}
              />
            </div>
          </div>

          {/* Success state */}
          {status === "success" && (
            <div className="text-center py-4" data-testid="reset-success-panel">
              <div className="w-16 h-16 bg-emerald-50 border border-emerald-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <CheckCircle size={28} className="text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Password updated</h2>
              <p className="text-sm text-slate-600 mb-6">
                Your password has been reset successfully. Redirecting you to your dashboard...
              </p>
              <Loader2 className="animate-spin w-5 h-5 text-blue-600 mx-auto" />
            </div>
          )}

          {/* Invalid token state */}
          {status === "invalid" && (
            <div className="text-center py-4" data-testid="reset-invalid-panel">
              <div className="w-16 h-16 bg-red-50 border border-red-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <XCircle size={28} className="text-red-500" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Invalid reset link</h2>
              <p className="text-sm text-slate-600 mb-8">
                This password reset link is invalid or has already been used.
              </p>
              <Button
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => navigate("/auth")}
                data-testid="back-to-auth-btn"
              >
                Back to sign in
              </Button>
            </div>
          )}

          {/* Expired token state */}
          {status === "expired" && (
            <div className="text-center py-4" data-testid="reset-expired-panel">
              <div className="w-16 h-16 bg-amber-50 border border-amber-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <XCircle size={28} className="text-amber-500" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Link expired</h2>
              <p className="text-sm text-slate-600 mb-8">
                This reset link has expired. Links are valid for 1 hour. Please request a new one.
              </p>
              <Button
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => navigate("/auth")}
                data-testid="request-new-link-btn"
              >
                Request new reset link
              </Button>
            </div>
          )}

          {/* Reset form */}
          {status === "form" && (
            <div data-testid="reset-password-form">
              <div className="w-14 h-14 bg-blue-50 border border-blue-100 rounded-full flex items-center justify-center mb-5">
                <KeyRound size={24} className="text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-1">Set new password</h2>
              <p className="text-sm text-slate-600 mb-6">
                Choose a strong password for your account.
              </p>

              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="new-password">New Password</Label>
                  <div className="relative mt-1">
                    <Input
                      id="new-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Min 6 characters"
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      required
                      data-testid="new-password-input"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <div className="relative mt-1">
                    <Input
                      id="confirm-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Repeat your password"
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      required
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={loading}
                  data-testid="reset-submit-btn"
                >
                  {loading ? <Loader2 className="animate-spin mr-2 w-4 h-4" /> : null}
                  Reset Password
                </Button>
              </form>

              <p className="mt-6 text-center text-sm text-slate-600">
                Remember it now?{" "}
                <button
                  onClick={() => navigate("/auth")}
                  className="text-blue-600 font-medium hover:underline"
                  data-testid="back-to-signin-link"
                >
                  Sign in
                </button>
              </p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
